import { pickProps } from "simple-utils"
import { Bot, load } from "./bot-loaders"
import { formatDatetime } from "simple-utils/dist/date-utils"

export type CampaignStatus = "none" | "preparing" | "prepared" | "running" | "done" | "scheduled" | "paused"

export function campaingStatusDescription(s:CampaignStatus){
    switch(s){
        case "none": return "Criado"
        case "preparing": return "Preparando Envio"
        case "prepared": return "Preparado para envio"
        case "running": return "Enviando"
        case "done": return "Concluído"
        case "scheduled": return "Agendado"
        case "paused": return "Pausado"
        default: return s;
    }
}

export interface Campaign {
    id: number
    bot: number
    audience: number
    audienceName: string
    name: string
    template: string
    media: number
    created: Date
    scheduled: Date
    status: CampaignStatus
    settings?: CampaignSettings
}

export interface CampaignSettings {
    speed?: "normal" | "fast" | "slow"
}

export interface CampaignRequest {
    audience: number
    name: string
    template?: string
    media: number
    settings?: CampaignSettings
}

function fixDate(i:Campaign):Campaign {
    return {...i, created: new Date(i.created), scheduled: i.scheduled ? new Date(i.scheduled) : null}
}

export async function listCampaigns(bot:Bot){
    const list = await load(`api/bot/${bot.id}/campaigns`, null, bot.token);
    return (list.result as Campaign[]).map(i => fixDate(i));
}

export async function addCampaign(bot: Bot, campaign:CampaignRequest){
    const a = await load(`api/bot/${bot.id}/campaigns/add`, pickProps(campaign, ["name", "audience", "template", "media", "settings"]), bot.token);
    return fixDate(a.result as Campaign);
}

export async function loadCampaign(bot: Bot, id:number):Promise<Campaign>{
    const a = await load(`api/bot/${bot.id}/campaigns/${id}`, null, bot.token);
    return fixDate(a.result as Campaign);
}

export async function updateCampaign(bot: Bot, campaign:Campaign):Promise<Campaign>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}`, pickProps(campaign, ["name", "audience", "template", "media", "settings"]), bot.token);
    return fixDate(a.result as Campaign);
}

export async function deleteCampaign(bot: Bot, campaign:Campaign){
    await load(`api/bot/${bot.id}/campaigns/${campaign.id}/delete`, null, bot.token);
}

export async function prepareCampaign(bot: Bot, campaign:Campaign):Promise<TaskStatus>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/prepare`, null, bot.token);
    return a.status;
}

export async function startCampaign(bot: Bot, campaign:Campaign):Promise<TaskStatus>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/run`, null, bot.token);
    return a.status;
}

export async function pauseCampaign(bot: Bot, campaign:Campaign):Promise<TaskStatus>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/pause`, null, bot.token);
    return a.status;
}

export async function scheduleCampaign(bot: Bot, campaign:Campaign, scheduleDate: string):Promise<Date>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/schedule`, {scheduleDate}, bot.token);
    return a.result ? new Date(a.result) : null;
}

export async function checkCampaignInteraction(bot: Bot, campaign:Campaign):Promise<TaskStatus>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/check-interaction`, null, bot.token);
    return a.status;
}

export async function statusCampaign(bot: Bot, campaign:Campaign):Promise<{status: CampaignStatus, taskStatus: TaskStatus}>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/status`, null, bot.token);
    return a.result;
}

export async function progressStatusCampaign(bot: Bot, campaign:Campaign):Promise<{status: CampaignStatus, taskStatus: TaskStatus, eventToken: string}>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/progress-status`, null, bot.token);
    return a.result;
}

export async function messageStatus(bot: Bot, campaign:Campaign):Promise<CampaignMessageResult[]>{
    const a = await load(`api/bot/${bot.id}/campaigns/${campaign.id}/message-status`, null, bot.token);
    return a.result.map((m:any) => new CampaignMessageResult(m));
}

export type TaskStatus = {total?: number, done?: number, error?:number, complete?:boolean}

export class CampaignMessageResult {
    member: number
    sent: Date
    read: Date
    delivered: Date
    interaction: Date

    constructor(m: {member:number, sent:string, read: string, delivered: string, interaction: string}){
        this.member = m.member
        this.sent = m.sent ? new Date(m.sent) : null
        this.delivered = m.delivered ? new Date(m.delivered) : null
        this.read = m.read ? new Date(m.read) : null
        this.interaction = m.interaction ? new Date(m.interaction) : null
    }

    get isDelivered() {return this.delivered != null}
    get isRead() {return this.read != null}
    get isSent() {return this.sent != null}
    get hasInteraction() {return this.interaction != null}

    get readStr() {return formatDatetime(this.read)}
    get deliveredStr() {return formatDatetime(this.delivered)}

    get groupName():string {
        if(this.hasInteraction) return "interaction"
        if(this.isRead) return "read"
        if(this.isDelivered) return "delivered"
        if(this.isSent) return "sent"
        return "other";
    }
}